import {
  Button,
  ActionIconGroup,
  ActionIcon,
  Stack,
  Divider,
  Select,
  Checkbox,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconCalendarCog, IconLayoutGrid, IconTable } from "@tabler/icons-react"
import dayjs from "dayjs"
import { DefaultModal } from "../../../../../-components/default-modal"
import { RemoteSelect } from "../../../../../-components/entity-select"
import { Page } from "../../../../../-components/Page"
import { RangePicker } from "../../../../../-components/range-picker"
import { DatePickerMakroChip } from "../../../../../-components/range-picker/datepicker-makro-chip"
import { SeasonQuickSelectMakro } from "../../../../../-components/range-picker/season-quickselect-makro"
import {
  eAppointmentCountingMethod,
  eAppointmentCountingMethod_AllValues,
} from "../../../../../@client/Theasoft_ApiServer_Calendar"
import { useBreakpoints } from "../../../../../utils/use-breakpoints"
import { useTranslations } from "../../../../../utils/use-t"
import { Calendar } from "../../../-components/calendar"
import { useUserLocalStorage } from "../../../-components/use-user-local-storage"

export function CalendarPage() {
  const { xs, sm } = useBreakpoints()
  const [start, setStart] = React.useState(new Date())

  const [end, setEnd] = React.useState(new Date())

  React.useEffect(() => {
    if (xs || sm) {
      setEnd(dayjs(new Date()).add(2, "months").toDate())
    } else {
      setEnd(dayjs(new Date()).add(11, "months").toDate())
    }
  }, [xs, sm])

  const [tempStart, setTempStart] = React.useState(start)
  const [tempEnd, setTempEnd] = React.useState(end)
  const [isTableView, { open: activateTableView, close: deactivateTableView }] =
    useDisclosure(true)

  const [opened, { open, close }] = useDisclosure(false)

  const {
    appServer: { t },
  } = useTranslations()

  const [appointmentCountMethod, setAppointmentCountMethod] =
    React.useState<eAppointmentCountingMethod | null>(null)

  const [tempAppointmentCountMethod, setTempAppointmentCountMethod] =
    React.useState<eAppointmentCountingMethod | null>(null)

  // Load saved appointmentCountMethod from localStorage
  React.useEffect(() => {
    const savedMethod = localStorage.getItem("appointmentCountMethod")
    if (
      savedMethod &&
      eAppointmentCountingMethod_AllValues.includes(
        savedMethod as eAppointmentCountingMethod
      )
    ) {
      setAppointmentCountMethod(savedMethod as eAppointmentCountingMethod)
    } else {
      setAppointmentCountMethod(eAppointmentCountingMethod_AllValues[1])
    }
  }, [])

  // Save appointmentCountMethod to localStorage whenever it changes
  React.useEffect(() => {
    if (appointmentCountMethod) {
      localStorage.setItem("appointmentCountMethod", appointmentCountMethod)
    }
  }, [appointmentCountMethod])

  const availableCountingMethods = [
    {
      value: eAppointmentCountingMethod_AllValues[1],
      label: t("CountAll"),
    },
    {
      value: eAppointmentCountingMethod_AllValues[2],
      label: t("CountPerformances"),
    },
  ]

  const handleConfirm = () => {
    setStart(tempStart)
    setEnd(tempEnd)
    if (tempAppointmentCountMethod) {
      setAppointmentCountMethod(tempAppointmentCountMethod)
    }
    close()
  }

  React.useEffect(() => {
    // Sync temporary state with the main state when the modal opens
    if (opened) {
      setTempAppointmentCountMethod(appointmentCountMethod)
    }
  }, [opened, appointmentCountMethod])

  const today = dayjs()

  const { active } = useBreakpoints()

  const buttonFormatString = active === "xs" ? "MMM YY" : "MMMM YYYY"

  const [showEndTime, setShowEndTime] = useUserLocalStorage<boolean>({
    key: "calendar-show-end-time",
    defaultValue: false,
  })

  return (
    <>
      <Page
        buttonGroupProps={{
          children: (
            <>
              <Button
                variant="subtle"
                px="xs"
                rightSection={<IconCalendarCog />}
                onClick={() => {
                  setTempStart(start)
                  setTempEnd(end)
                  open()
                }}
              >
                {`${dayjs(start).format(buttonFormatString)} - ${dayjs(end).format(buttonFormatString)}`}
              </Button>
              <ActionIconGroup>
                <ActionIcon
                  disabled={!isTableView}
                  onClick={() => deactivateTableView()}
                >
                  <IconLayoutGrid />
                </ActionIcon>
                <ActionIcon
                  disabled={isTableView}
                  onClick={() => activateTableView()}
                >
                  <IconTable />
                </ActionIcon>
              </ActionIconGroup>
            </>
          ),
        }}
      >
        <Calendar
          startDate={start}
          endDate={end}
          tableView={isTableView}
          appointmentCountMethod={
            appointmentCountMethod ?? eAppointmentCountingMethod_AllValues[1]
          }
        />
      </Page>
      <DefaultModal
        opened={opened}
        onClose={close}
        title={`${t("Calendar")} ${t("Settings")}`}
      >
        <React.Suspense>
          <Stack gap="sm">
            <RangePicker
              variant="month"
              start={tempStart}
              setEnd={(newDate) => {
                if (newDate) {
                  setTempEnd(newDate)
                }
              }}
              end={tempEnd}
              setStart={(newDate) => {
                if (newDate) {
                  setTempStart(newDate)
                }
              }}
            />
            <Divider />
            <SeasonQuickSelectMakro
              start={tempStart}
              setEnd={(newDate) => {
                if (newDate) {
                  setTempEnd(newDate)
                }
              }}
              end={tempEnd}
              setStart={(newDate) => {
                if (newDate) {
                  setTempStart(newDate)
                }
              }}
            />
            <Divider />
            <RemoteSelect
              url="/api/Season/GetSeasonMakroPeriods"
              entitiesToComboboxData={(entities) =>
                entities.map((entity) => ({
                  label: entity.name,
                  value: entity.seasonId.toString(),
                }))
              }
              input={null}
              onChange={(season) => {
                if (season) {
                  setTempStart(dayjs(season.startDate).toDate())
                  setTempEnd(dayjs(season.endDate).toDate())
                }
              }}
              valueToEntity={(value, entities) =>
                entities.find((entity) => entity.seasonId.toString() === value)
              }
              label={`${t("Seasons")} / ${t("CalendarYear")}`}
            />
            <Divider />

            <DatePickerMakroChip
              key={"from-now-makro"}
              styles={{
                iconWrapper: {
                  display: "none",
                },
                label: {
                  padding: "0 var(--mantine-spacing-md)",
                },
              }}
              checked={
                dayjs(tempStart).isSame(
                  today.startOf("month").toDate(),
                  "date"
                ) &&
                dayjs(tempEnd).isSame(
                  today.add(11, "months").endOf("month").toDate(),
                  "date"
                )
              }
              variant="filled"
              children={t("OneYearFromCurrentMonth")}
              targetStart={today.startOf("month").toDate()}
              targetEnd={today.add(11, "months").endOf("month").toDate()}
              start={start}
              end={end}
              setEnd={(newDate) => {
                if (newDate) {
                  setTempEnd(newDate)
                }
              }}
              setStart={(newDate) => {
                if (newDate) {
                  setTempStart(newDate)
                }
              }}
            />
            <Divider />

            <Select
              label={t("AppointmentCountMode")}
              value={
                tempAppointmentCountMethod ??
                eAppointmentCountingMethod_AllValues[1]
              }
              onChange={(newVal) =>
                setTempAppointmentCountMethod(
                  newVal ? (newVal as eAppointmentCountingMethod) : "NoCount"
                )
              }
              data={availableCountingMethods}
            />

            <Divider />
            <Checkbox
              label={t("ShowEndTime")}
              checked={showEndTime}
              onChange={(e) => setShowEndTime(e.currentTarget.checked)}
            />
            <Button
              style={{ alignSelf: "end" }}
              w="fit-content"
              onClick={handleConfirm}
            >
              {t("Confirm")}
            </Button>
          </Stack>
        </React.Suspense>
      </DefaultModal>
    </>
  )
}
